/**
 * Frontend Permissions Configuration for Admin Panel
 * 
 * This module defines which roles can access which features
 * and provides utilities for permission-based UI rendering.
 */

// All roles that can access the Admin Panel
export const ADMIN_PANEL_ROLES = [
    'admin',
    'tech_leader',
    'hod',
    'finance',
    'cocurricular',
    'management'
];

// Role display names for UI
export const ROLE_DISPLAY_NAMES = {
    'admin': 'Leader',
    'tech_leader': 'Tech Leader',
    'hod': 'Head of Department',
    'finance': 'Finance',
    'cocurricular': 'Co-curricular',
    'management': 'Management'
};

// Menu items with their allowed roles.
// Section headers are presentation-only; they never alter route access.
//
// Sections, groups, and item names/order below follow
// Requirements/NEW-UI/Admin-Sidebar. No previously-reachable real page is
// removed or made unreachable — every page that existed in the sidebar
// before this restructure still has an entry. Where the reference names a
// group that doesn't exist here (SOPs, Affiliation Laws, Examinations
// Planning, Teacher's Appraisal, Future Ready Reports, Self Awareness,
// Time Management), that item is added pointing at /ComingSoon so the
// reference's exact item list is present without inventing a fake page.
// "Overview" (Analytics / Branch Overview / Department Overview /
// Activities Dashboard) has no reference equivalent but is kept since it's
// real, reachable functionality.
export const MENU_ITEMS = [
    { key: 'home', text: 'Home', path: '/HomePage', allowedRoles: null },

    { key: 'section_overview', text: 'Overview', isSectionHeader: true, allowedRoles: null },
    { key: 'analytics', text: 'Analytics', path: '/analytics', allowedRoles: ['admin', 'management'] },
    { key: 'management_dashboard', text: 'Branch Overview', path: '/ManagementDashboard', allowedRoles: ['admin', 'management'] },
    // 'management' added: this page is also where the second, independent
    // lesson-plan approval stage lives (HODDashboard.js's "Admin Approval"
    // tab, restricted in-page to admin/management) — without it here,
    // management could never even reach the route to use that tab.
    { key: 'hod_dashboard', text: 'Department Overview', path: '/HODDashboard', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'cocurricular_dashboard', text: 'Activities Dashboard', path: '/ComingSoon', allowedRoles: ['cocurricular'] },

    // === SETUP ===
    { key: 'section_setup', text: 'Setup', isSectionHeader: true, allowedRoles: null },
    {
        key: 'onboarding', text: 'Onboarding', path: '/viewteachers', isGroupHeader: true, group: 'onboarding',
        allowedRoles: ['admin', 'tech_leader', 'hod', 'management']
    },
    { key: 'teachers', text: 'Teachers', path: '/viewteachers', group: 'onboarding', allowedRoles: ['admin', 'tech_leader', 'hod', 'management'] },
    { key: 'students', text: 'Students', path: '/viewstudents', group: 'onboarding', allowedRoles: ['admin', 'tech_leader', 'management'] },
    { key: 'parents', text: 'Parents', path: '/viewparents', group: 'onboarding', allowedRoles: ['admin', 'tech_leader', 'management'] },
    { key: 'hods', text: 'HODs', path: '/viewhods', group: 'onboarding', allowedRoles: ['admin', 'management', 'tech_leader'] },
    { key: 'finance_staff', text: 'Finance Staff', path: '/viewfinance', group: 'onboarding', allowedRoles: ['admin', 'management', 'tech_leader'] },
    { key: 'cocurricular_staff', text: 'Co-Curricular Staff', path: '/viewcocurricular', group: 'onboarding', allowedRoles: ['admin', 'management', 'tech_leader'] },
    { key: 'tech_leaders', text: 'Tech Staff', path: '/viewtechleaders', group: 'onboarding', allowedRoles: ['admin', 'management', 'tech_leader'] },
    { key: 'ClassManagement', text: 'Class Management', path: '/ClassManagement', group: 'onboarding', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'departments', text: 'Departments Management', path: '/DepartmentManagement', group: 'onboarding', allowedRoles: ['admin', 'management'] },

    // === PLANNING & COMPLIANCE ===
    { key: 'section_planning', text: 'Planning & Compliance', isSectionHeader: true, allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'holistic_auditing', text: 'Holistic School Auditing', path: '/SQAAF', allowedRoles: ['admin', 'management'] },
    {
        key: 'school_compliances', text: 'School Compliances', path: '/CBSECirculars', isGroupHeader: true, group: 'school_compliances',
        allowedRoles: ['admin', 'hod', 'management']
    },
    { key: 'CBSECirculars', text: 'CBSE Circulars', path: '/CBSECirculars', group: 'school_compliances', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'sops', text: 'SOPs', path: '/ComingSoon', group: 'school_compliances', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'affiliation_laws', text: 'Affiliation Laws', path: '/ComingSoon', group: 'school_compliances', allowedRoles: ['admin', 'hod', 'management'] },
    {
        key: 'sdp', text: 'School Destiny Planning', path: '/sdp/plans', isGroupHeader: true, group: 'sdp',
        allowedRoles: ['admin', 'management']
    },
    { key: 'sdp_plans', text: 'All Plans', path: '/sdp/plans', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_dashboard', text: 'Dashboard', path: '/sdp/dashboard', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_quick_start', text: 'AI Quick Start', path: '/sdp/quick-start', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_identity', text: 'Institutional Identity', path: '/sdp/institutional-identity', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_school_info', text: 'School Information', path: '/sdp/school-information', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_expenditure', text: 'Expenditure', path: '/sdp/expenditure', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_teams', text: 'Teams & Committees', path: '/sdp/teams-committees', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_market', text: 'Market Dynamics', path: '/sdp/market-dynamics', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_diagnosis', text: 'Diagnosis', path: '/sdp/diagnosis', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_swot', text: 'SWOT Analysis', path: '/sdp/swot', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_goals', text: 'Goals', path: '/sdp/goals', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_actions', text: 'Action Plans', path: '/sdp/action-plans', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_monitoring', text: 'Monitoring', path: '/sdp/monitoring', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_reports', text: 'Reports & Summary', path: '/sdp/reports-summary', group: 'sdp', allowedRoles: ['admin', 'management'] },
    { key: 'sdp_complete', text: 'Complete School View', path: '/sdp/complete-school-view', group: 'sdp', allowedRoles: ['admin', 'management'] },

    {
        key: 'acpp', text: 'School Annual Planning', path: '/acpp/plans', isGroupHeader: true, group: 'annual_planning',
        allowedRoles: ['admin', 'hod', 'management']
    },
    { key: 'acpp_plans', text: 'All Plans', path: '/acpp/plans', group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_m1',  text: 'School Master',       path: '/acpp/module1',  group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_m2',  text: 'Setup Logic',         path: '/acpp/module2',  group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_m3',  text: 'Grades Offered',      path: '/acpp/module3',  group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_m4',  text: 'Academic Planning',   path: '/acpp/module4',  group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_m5',  text: 'Ownership Mapping',   path: '/acpp/module5',  group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_m6',  text: 'Teacher Workflow',    path: '/acpp/module6',  group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_approvals', text: 'Teacher Workflow Approvals', path: '/acpp/approvals', group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_m7',  text: 'ACPP Assessment Outline', path: '/acpp/module7',  group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_m8',  text: 'Teacher Development', path: '/acpp/module8',  group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_m9',  text: 'Student Development', path: '/acpp/module9',  group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_m10', text: 'Academic Calendar',   path: '/acpp/module10', group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_m11', text: 'Dynamic Timetable',   path: '/acpp/module11', group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_m12', text: 'Leadership Dashboard', path: '/acpp/module12', group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'acpp_m13', text: 'ACPP Lesson Plan Outline', path: '/acpp/module13', group: 'annual_planning', allowedRoles: ['admin', 'hod', 'management'] },

    { key: 'CalendarManagement', text: 'Calendar Management', path: '/CalendarManagement', allowedRoles: ['admin', 'management'] },

    // === TRACKING & REPORTS ===
    { key: 'section_tracking', text: 'Tracking & Reports', isSectionHeader: true, allowedRoles: ['admin', 'hod', 'management'] },
    {
        key: 'teacher_tracker', text: "Teacher's Tracker", path: '/GrowthOverview', isGroupHeader: true, group: 'teacher_tracker',
        allowedRoles: ['admin', 'hod', 'management']
    },
    { key: 'growth_overview', text: 'Complete Overview', path: '/GrowthOverview', group: 'teacher_tracker', allowedRoles: ['admin', 'management'] },
    { key: 'LessonPlanManagement', text: 'Lesson Plans', path: '/LessonPlanManagement', group: 'teacher_tracker', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'AssessmentManagement', text: 'Assessments Management', path: '/AssessmentManagement', group: 'teacher_tracker', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'CompetencyManagement', text: 'Competencies Achieved', path: '/CompetencyManagement', group: 'teacher_tracker', allowedRoles: ['admin', 'hod', 'management'] },
    { key: 'teacher_appraisal', text: "Teacher's Appraisal", path: '/ComingSoon', group: 'teacher_tracker', allowedRoles: ['admin', 'hod', 'management'] },
    {
        key: 'student_reporting', text: "Student's Reporting", path: '/AcademicTranscriptReports', isGroupHeader: true, group: 'student_reporting',
        allowedRoles: ['admin', 'hod', 'management']
    },
    { key: 'academic_transcript_reports', text: 'Academic Reports', path: '/AcademicTranscriptReports', group: 'student_reporting', allowedRoles: ['admin', 'management', 'hod'] },
    { key: 'growth_achievement_reports', text: 'Future Ready Reports', path: '/GrowthAchievementReports', group: 'student_reporting', allowedRoles: ['admin', 'management', 'hod'] },
    { key: 'HPCManagement', text: 'Holistic Progress Cards', path: '/HPCManagement', group: 'student_reporting', allowedRoles: ['admin', 'management'] },
    { key: 'student_reports_centre', text: 'All Three Combined Reports', path: '/StudentReportsCentre', group: 'student_reporting', allowedRoles: ['admin', 'management', 'hod'] },
    { key: 'leaderboard', text: "Student's Leaderboard", path: '/leaderboard', group: 'student_reporting', allowedRoles: ['admin', 'management', 'hod'] },

    // === REVENUE ===
    { key: 'section_boosters', text: 'Revenue', isSectionHeader: true, allowedRoles: ['admin', 'management', 'finance'] },
    { key: 'finance_dashboard', text: 'Fees & Finance', path: '/finance/dashboard', allowedRoles: ['finance', 'admin', 'management'] },
    { key: 'admission_booster', text: 'Admissions Management', path: '/admission-booster', allowedRoles: ['admin', 'management'] },

    // === FOR YOU ===
    { key: 'section_account', text: 'For You', isSectionHeader: true, allowedRoles: null },
    {
        key: 'leaders_self_growth', text: 'Leaders Self Growth', path: '/Profile', isGroupHeader: true, group: 'leaders_self_growth',
        allowedRoles: null
    },
    { key: 'profile', text: 'Profile', path: '/Profile', group: 'leaders_self_growth', allowedRoles: null },
    { key: 'self_awareness', text: 'Self Awareness', path: '/ComingSoon', group: 'leaders_self_growth', allowedRoles: null },
    { key: 'time_management', text: 'Time Management', path: '/EisenhowerMatrix', group: 'leaders_self_growth', allowedRoles: null },

    { key: 'logout', text: 'Logout', path: 'Logout', allowedRoles: null },
];

/**
 * Check if a role can access a specific menu item
 * @param {string} userRole - The user's role
 * @param {string[]|null} allowedRoles - Array of allowed roles, or null for all roles
 * @returns {boolean}
 */
export const canAccessMenuItem = (userRole, allowedRoles) => {
    // If no restrictions, allow all admin panel roles
    if (!allowedRoles) {
        return ADMIN_PANEL_ROLES.includes(userRole);
    }
    return allowedRoles.includes(userRole);
};

/**
 * Get filtered menu items for a specific role
 * @param {string} userRole - The user's role
 * @returns {Array} - Filtered menu items
 */
export const getMenuItemsForRole = (userRole) => {
    const visibleItems = MENU_ITEMS.filter(item => canAccessMenuItem(userRole, item.allowedRoles));

    // Keep section labels only when this role can see an item in that section.
    // This prevents Finance, HOD, and specialist roles from seeing empty headings.
    return visibleItems.filter((item, index) => {
        if (!item.isSectionHeader) return true;

        const itemsUntilNextSection = visibleItems.slice(index + 1);
        const nextSectionIndex = itemsUntilNextSection.findIndex(nextItem => nextItem.isSectionHeader);
        const sectionItems = nextSectionIndex === -1
            ? itemsUntilNextSection
            : itemsUntilNextSection.slice(0, nextSectionIndex);

        return sectionItems.some(sectionItem => !sectionItem.isSectionHeader);
    });
};

/**
 * Check if user has an admin panel role
 * @param {string} role 
 * @returns {boolean}
 */
export const isAdminPanelRole = (role) => {
    return ADMIN_PANEL_ROLES.includes(role);
};

const permissionsConfig = {
    ADMIN_PANEL_ROLES,
    ROLE_DISPLAY_NAMES,
    MENU_ITEMS,
    canAccessMenuItem,
    getMenuItemsForRole,
    isAdminPanelRole
};

export default permissionsConfig;
